import { useEffect, useMemo, useState, useRef } from 'react';

import { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { blockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import { editorViewModePlugin } from '@atlaskit/editor-plugin-editor-viewmode';
import { engagementPlatformPlugin } from '@atlaskit/editor-plugin-engagement-platform';
import { connectivityPlugin } from '@atlaskit/editor-plugin-connectivity';
import { contextMenuPlugin } from '@atlaskit/editor-plugin-context-menu';
import { editorViewModeEffectsPlugin } from '@atlaskit/editor-plugin-editor-viewmode-effects';
import { loomPlugin } from '@atlaskit/editor-plugin-loom';
import { highlightPlugin } from '@atlaskit/editor-plugin-highlight';
import { metricsPlugin } from '@atlaskit/editor-plugin-metrics';
import { selectionMarkerPlugin } from '@atlaskit/editor-plugin-selection-marker';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { EditorProps } from '@atlaskit/editor-core';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import { codeBlockAdvancedPlugin } from '@atlaskit/editor-plugin-code-block-advanced';

import { PageNextEditorPlugin } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { aiDefinitionsPlugin } from '@atlassian/editor-plugin-ai-definitions';
import { referentialityPlugin } from '@atlassian/editor-referentiality';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider';
import type { createPageEditorPluginAIProvider } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import { type LoomProviderConfig } from '@confluence/fabric-providers';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import {
	type MentionType,
	useMentionsNotifications,
	handleMentionsChanged,
} from '@confluence/mentions-notifications';
import { useSessionData } from '@confluence/session-data';
import {
	EditorHeadingLinksPlugin,
	getIsHeadingLinksEnabled,
} from '@confluence/editor-heading-links';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage';
import type { WithFlagsProps } from '@confluence/flags';
import { useInitializeLoomPlugin, useLoomPluginOptions } from '@confluence/loom-editor-utils';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import {
	useEditorEngagementPlatform,
	useEditorPluginAIDefinitionsConfig,
	useEditorPluginAIDefinitionsEnabled,
} from '@confluence/editor-features';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { isConfluenceRedirectToLoomSDKExperimentEnabled } from '@confluence/growth-experiment-helpers';
import type { ReferentialityContext } from '@confluence/app-referentiality';
import type { AIGlobalOptIn } from '@confluence/editor-features';
import type { FullPageAppearances } from '@confluence/content-appearance';

import { EditorLoomButton as Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton } from './Experiment_RedirectToSdkAfterCrossflow/EditorLoomButton';
import { ssrPlugin } from './plugins/ssrPlugin';
import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin';
import { EditorLoomButton } from './EditorLoomButton';
import { emptyStatePlugin } from './plugins/emptyStatePlugin';

export interface FullPageEditorProps extends EditorProps {
	referentialityContext: ReferentialityContext;
	editorPluginAIProvider: ReturnType<typeof createPageEditorPluginAIProvider>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPILegacy) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	appearance: FullPageAppearances;
	getViewPagePath: () => string | null;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
}

export type FullPageEditorPresetAPILegacy = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorProps & WithFlagsProps): any {
	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();
	const { isVariantCohort: isConfluenceRedirectToLoomSDKExperiment } =
		isConfluenceRedirectToLoomSDKExperimentEnabled(); // evaluate experiment here but does NOT fire exposure event

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);

	useEffect(() => {
		if (mentionChanges.length && isLivePage && fg('confluence_updated_mentions_livepages')) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const mentionsPluginInitialConfiguration = {
		handleMentionsChanged: (mentionChanges: MentionType[]) => {
			if (mentionChanges.length) {
				setMentionChanges(mentionChanges);
			}
		},
	};

	const tasksAndDecisionsInitialConfiguration = fg('editor_request_to_edit_task')
		? {
				hasEditPermission: props.hasEditPermission,
				requestToEditContent: props.requestToEditContent,
				hasRequestedEditPermission: props.hasRequestedEditPermission,
			}
		: undefined;

	const extensionPluginInitialConfiguration = fg(
		'confluence_bodied_macros_rendered_view_live_pages',
	)
		? {
				__rendererExtensionOptions: props.__rendererExtensionOptions,
			}
		: undefined;

	const initialPluginConfiguration = {
		mentionsPlugin: mentionsPluginInitialConfiguration,
		tasksAndDecisionsPlugin: tasksAndDecisionsInitialConfiguration,
		extensionPlugin: extensionPluginInitialConfiguration,
	};
	const universalPreset = useUniversalPreset({
		props,
		initialPluginConfiguration,
	});

	const isHeadingLinksEnabled = getIsHeadingLinksEnabled(true);
	const textHighlightingFloatingToolbarExperiment =
		props.aiGlobalOptIn.status === 'disabled' &&
		expVal<boolean>('editor_text_highlighting_floating_toolbar', 'isEnabled', false);

	const editorAPIRef = useRef<FullPageEditorPresetAPILegacy | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: isConfluenceRedirectToLoomSDKExperiment
				? Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton
				: EditorLoomButton,
			editorAPIRef,
		});

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	// Memoise the preset otherwise we will re-render the editor too often
	const { preset, editorApi } = usePreset(() => {
		const { editorPluginAIProvider, aiGlobalOptIn } = props;
		return universalPreset
			.maybeAdd(
				[
					referentialityPlugin,
					{
						...props.referentialityContext.options,
						api: props.referentialityContext.api ?? undefined,
					},
				],
				Boolean(props.referentialityContext.plugin),
			)
			.maybeAdd(
				[
					PageNextEditorPlugin,
					{
						editorPluginAIProvider,
						aiGlobalOptIn,
					},
				],
				Boolean(aiGlobalOptIn.status !== 'disabled'),
			)
			.maybeAdd([loomPlugin, loomPluginOptions], isLoomPluginEnabled)
			.add([editorViewModePlugin, { mode: props.viewMode }])
			.add(editorViewModeEffectsPlugin)
			.maybeAdd(
				// aiDefinitionsPlugin depends on editorViewModePlugin
				[aiDefinitionsPlugin, editorPluginAIDefinitionsConfig],
				editorPluginAIDefinitionsEnabled,
			)
			.add(selectionMarkerPlugin)
			.add(ssrPlugin)
			.maybeAdd(blockControlsPlugin, fg('platform_editor_element_drag_and_drop'))
			.maybeAdd(
				[
					EditorHeadingLinksPlugin,
					{
						getViewPagePath: props.getViewPagePath,
					},
				],
				isHeadingLinksEnabled,
			)
			.add([highlightPlugin, { textHighlightingFloatingToolbarExperiment }])
			.maybeAdd(contextMenuPlugin, fg('platform_editor_context-menu-plugin'))
			.maybeAdd(pageTitleUtilsPlugin, fg('confluence_frontend_page_title_enter_improvements'))
			.add(fabricExtensionManagerPlugin)
			.add([engagementPlatformPlugin, editorEngagementPlatformPluginConfig])
			.add(forgeEditorPlugin)
			.maybeAdd(connectivityPlugin, isDesktop || fg('platform_editor_offline_editing_mvp'))
			.maybeAdd(editorOfflineStoragePlugin, isDesktop || fg('platform_editor_offline_editing_ga'))
			.add(emptyStatePlugin)
			.maybeAdd(metricsPlugin, fg('platform_editor_ease_of_use_metrics'))
			.maybeAdd(
				codeBlockAdvancedPlugin,
				expVal('platform_editor_advanced_code_blocks', 'isEnabled', false),
			);
		// The only thing that cause a re-creation of the preset is something in the
		// universal preset to be consistent with current behaviour (ie. this could
		// be a page width change via the `appearance` prop).
	}, [
		universalPreset,
		props.aiGlobalOptIn.status,
		isLoomPluginEnabled,
		loomPluginOptions,
		isHeadingLinksEnabled,
		props.getViewPagePath,
		editorEngagementPlatformPluginConfig,
	]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom,
		providerConfig: loomProviderConfig,
	});

	return preset;
}
